import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronDown, X, Sparkles } from 'lucide-react';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const DAY_NAMES = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/**
 * Parses YYYY-MM-DD string into { year, monthIndex (0-11), day (1-31) }
 * Avoids any timezone-induced date shifts.
 */
const parseDateParts = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.trim().split('-');
  if (parts.length !== 3) return null;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  if (isNaN(y) || isNaN(m) || isNaN(d) || m < 0 || m > 11 || d < 1 || d > 31) return null;
  return { year: y, month: m, day: d };
};

const formatDateString = (y, m, d) => {
  const mm = String(m + 1).padStart(2, '0');
  const dd = String(d).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
};

/**
 * Format date for friendly trigger display: "26 Oct 2026"
 */
const formatDisplayDate = (dateStr) => {
  const parts = parseDateParts(dateStr);
  if (!parts) return '';
  return `${parts.day} ${MONTH_SHORT[parts.month]} ${parts.year}`;
};

/**
 * PremiumDatePicker - A luxury, modern date picker component for Aptiview.
 * Matches Aptiview's gradient branding, smooth micro-animations, and clean typography.
 *
 * Fully drop-in compatible with standard <input type="date" />:
 * - value: string 'YYYY-MM-DD'
 * - min: string 'YYYY-MM-DD'
 * - max: string 'YYYY-MM-DD'
 * - disableFuture: boolean (defaults to true; strictly disallows dates after today)
 * - onChange: supports both (event) => ... and (dateStr) => ...
 * - compact: boolean (inline filter bar mode)
 * - prefix: string (e.g., "From:", "To:")
 */
const PremiumDatePicker = ({
  value = '',
  onChange,
  min = '',
  max = '',
  disableFuture = true,
  placeholder = 'dd-mm-yyyy',
  prefix = '',
  label = '',
  name = '',
  compact = false,
  disabled = false,
  align = 'left', // 'left' | 'right'
  dropUp = false,
  className = '',
  iconColor = 'text-blue-500',
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState('days'); // 'days' | 'months' | 'years'
  const containerRef = useRef(null);

  // Compute today's date in local time
  const today = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();
    return {
      year: y,
      month: m,
      day: d,
      str: formatDateString(y, m, d)
    };
  }, []);

  // Compute effective maximum date string (strictly disallowing future dates when disableFuture is true)
  const effectiveMax = useMemo(() => {
    if (disableFuture) {
      if (max && max < today.str) return max;
      return today.str;
    }
    return max || '';
  }, [max, disableFuture, today.str]);

  const maxParts = useMemo(() => parseDateParts(effectiveMax), [effectiveMax]);
  const minParts = useMemo(() => parseDateParts(min), [min]);

  // Selected date parts
  const selected = useMemo(() => parseDateParts(value), [value]);

  // Calendar view year and month
  const [viewYear, setViewYear] = useState(() => {
    if (selected) return selected.year;
    if (maxParts && today.year > maxParts.year) return maxParts.year;
    return today.year;
  });
  const [viewMonth, setViewMonth] = useState(() => {
    if (selected) return selected.month;
    if (maxParts && today.year === maxParts.year && today.month > maxParts.month) return maxParts.month;
    return today.month;
  });

  // Sync view when opened or value changed
  useEffect(() => {
    if (selected) {
      setViewYear(selected.year);
      setViewMonth(selected.month);
    } else if (effectiveMax) {
      const parts = parseDateParts(effectiveMax);
      if (parts && (viewYear > parts.year || (viewYear === parts.year && viewMonth > parts.month))) {
        setViewYear(parts.year);
        setViewMonth(parts.month);
      }
    }
  }, [value, effectiveMax]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setViewMode('days');
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setViewMode('days');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Check if a date string is disabled by min or effectiveMax
  const isDateDisabled = useCallback((dateStr) => {
    if (!dateStr) return false;
    if (min && dateStr < min) return true;
    if (effectiveMax && dateStr > effectiveMax) return true;
    return false;
  }, [min, effectiveMax]);

  // Navigation constraints
  const isNextMonthDisabled = useMemo(() => {
    if (!maxParts) return false;
    if (viewYear > maxParts.year) return true;
    if (viewYear === maxParts.year && viewMonth >= maxParts.month) return true;
    return false;
  }, [maxParts, viewYear, viewMonth]);

  const isPrevMonthDisabled = useMemo(() => {
    if (!minParts) return false;
    if (viewYear < minParts.year) return true;
    if (viewYear === minParts.year && viewMonth <= minParts.month) return true;
    return false;
  }, [minParts, viewYear, viewMonth]);

  // Dispatch change event to caller
  const triggerChange = (nextDateStr) => {
    if (nextDateStr && isDateDisabled(nextDateStr)) return;
    if (typeof onChange === 'function') {
      const syntheticEvent = {
        target: { value: nextDateStr, name },
        currentTarget: { value: nextDateStr, name },
        value: nextDateStr,
        preventDefault: () => {},
        stopPropagation: () => {},
        toString: () => nextDateStr,
      };
      onChange(syntheticEvent, nextDateStr);
    }
  };

  const handleSelectDate = (y, m, d) => {
    const dateStr = formatDateString(y, m, d);
    if (isDateDisabled(dateStr)) return;
    triggerChange(dateStr);
    setIsOpen(false);
    setViewMode('days');
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    triggerChange('');
    setIsOpen(false);
    setViewMode('days');
  };

  const handleSelectToday = () => {
    if (isDateDisabled(today.str)) return;
    triggerChange(today.str);
    setViewYear(today.year);
    setViewMonth(today.month);
    setIsOpen(false);
    setViewMode('days');
  };

  // Month navigation
  const handlePrevMonth = (e) => {
    e.stopPropagation();
    if (isPrevMonthDisabled) return;
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((prev) => prev - 1);
    } else {
      setViewMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    if (isNextMonthDisabled) return;
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((prev) => prev + 1);
    } else {
      setViewMonth((prev) => prev + 1);
    }
  };

  // Calendar days calculation (Monday as first day)
  const calendarDays = useMemo(() => {
    const firstDayOfWeek = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;
    const daysInCurrentMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const days = [];

    // Prev month days
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      const m = viewMonth === 0 ? 11 : viewMonth - 1;
      const y = viewMonth === 0 ? viewYear - 1 : viewYear;
      const dateStr = formatDateString(y, m, d);
      days.push({
        day: d,
        month: m,
        year: y,
        dateStr,
        isCurrentMonth: false,
        isDisabled: isDateDisabled(dateStr),
      });
    }

    // Current month days
    for (let d = 1; d <= daysInCurrentMonth; d++) {
      const dateStr = formatDateString(viewYear, viewMonth, d);
      days.push({
        day: d,
        month: viewMonth,
        year: viewYear,
        dateStr,
        isCurrentMonth: true,
        isDisabled: isDateDisabled(dateStr),
      });
    }

    // Next month days to pad to a 35 or 42 grid
    const totalSlots = days.length > 35 ? 42 : 35;
    const remainingSlots = totalSlots - days.length;
    for (let d = 1; d <= remainingSlots; d++) {
      const m = viewMonth === 11 ? 0 : viewMonth + 1;
      const y = viewMonth === 11 ? viewYear + 1 : viewYear;
      const dateStr = formatDateString(y, m, d);
      days.push({
        day: d,
        month: m,
        year: y,
        dateStr,
        isCurrentMonth: false,
        isDisabled: isDateDisabled(dateStr),
      });
    }

    return days;
  }, [viewYear, viewMonth, isDateDisabled]);

  // Year range for year picker view
  const yearPickerRange = useMemo(() => {
    const start = Math.floor(viewYear / 12) * 12;
    const list = [];
    for (let y = start - 1; y < start + 11; y++) {
      list.push(y);
    }
    return list;
  }, [viewYear]);

  // Check month disabled in Month Picker
  const isMonthDisabled = (idx) => {
    if (maxParts) {
      if (viewYear > maxParts.year) return true;
      if (viewYear === maxParts.year && idx > maxParts.month) return true;
    }
    if (minParts) {
      if (viewYear < minParts.year) return true;
      if (viewYear === minParts.year && idx < minParts.month) return true;
    }
    return false;
  };

  // Check year disabled in Year Picker
  const isYearDisabled = (yr) => {
    if (maxParts && yr > maxParts.year) return true;
    if (minParts && yr < minParts.year) return true;
    return false;
  };

  return (
    <div
      ref={containerRef}
      className={`relative inline-block select-none ${compact ? '' : 'w-full'} ${className}`}
      id={id}
    >
      {/* Optional Top Label */}
      {label && (
        <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-1.5">
          <CalendarIcon size={13} className={iconColor} />
          <span>{label}</span>
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setIsOpen((prev) => !prev);
            setViewMode('days');
          }
        }}
        className={`group flex items-center justify-between text-left transition-all cursor-pointer ${
          compact
            ? 'h-9 px-2.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50/70 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-2xs gap-1.5 text-xs'
            : 'w-full h-10 px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-700 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-xs text-sm'
        } ${isOpen ? 'ring-2 ring-blue-500/30 border-blue-500 bg-white' : ''} ${
          disabled ? 'opacity-60 cursor-not-allowed bg-gray-100' : ''
        }`}
      >
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <CalendarIcon
            size={compact ? 13 : 15}
            className={`shrink-0 transition-colors ${
              value ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'
            }`}
          />
          {prefix && (
            <span className="text-[11px] font-semibold text-gray-500 shrink-0">
              {prefix}
            </span>
          )}
          <span
            className={`truncate font-medium ${
              value
                ? 'text-gray-800 font-semibold'
                : 'text-gray-400 font-normal'
            } ${compact ? 'text-xs' : 'text-sm'}`}
          >
            {value ? formatDisplayDate(value) : placeholder}
          </span>
        </div>

        <div className="flex items-center gap-1 ml-1.5 shrink-0">
          {value && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleClear(e);
                }
              }}
              title="Clear date"
              className="p-0.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            >
              <X size={compact ? 12 : 14} />
            </span>
          )}
          <ChevronDown
            size={compact ? 12 : 14}
            className={`text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180 text-blue-600' : 'group-hover:text-gray-600'
            }`}
          />
        </div>
      </button>

      {/* Floating Calendar Popup */}
      {isOpen && (
        <div
          className={`absolute ${
            dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          } ${
            align === 'right' ? 'right-0' : 'left-0'
          } z-50 w-72 sm:w-80 bg-white rounded-2xl shadow-2xl border border-gray-200/90 overflow-hidden transform transition-all duration-200 animate-in fade-in zoom-in-95`}
          style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}
        >
          {/* Aptiview Gradient Accent Line */}
          <div className="h-1 w-full bg-gradient-to-r from-sky-400 via-indigo-500 to-fuchsia-500" />

          {/* Calendar Header */}
          <div className="px-4 py-3 bg-gradient-to-b from-gray-50/80 to-white border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setViewMode(viewMode === 'months' ? 'days' : 'months')}
                className="px-2 py-1 text-xs sm:text-sm font-bold text-gray-800 hover:text-blue-600 hover:bg-blue-50/60 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
              >
                <span>{MONTH_NAMES[viewMonth]}</span>
                <ChevronDown size={13} className="text-gray-400" />
              </button>

              <button
                type="button"
                onClick={() => setViewMode(viewMode === 'years' ? 'days' : 'years')}
                className="px-2 py-1 text-xs sm:text-sm font-bold text-gray-800 hover:text-blue-600 hover:bg-blue-50/60 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
              >
                <span>{viewYear}</span>
                <ChevronDown size={13} className="text-gray-400" />
              </button>
            </div>

            {/* Prev / Next Arrows */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={isPrevMonthDisabled}
                onClick={handlePrevMonth}
                className={`p-1.5 rounded-lg transition-colors ${
                  isPrevMonthDisabled
                    ? 'text-gray-300 opacity-30 cursor-not-allowed pointer-events-none'
                    : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50 cursor-pointer'
                }`}
                title="Previous Month"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                disabled={isNextMonthDisabled}
                onClick={handleNextMonth}
                className={`p-1.5 rounded-lg transition-colors ${
                  isNextMonthDisabled
                    ? 'text-gray-300 opacity-30 cursor-not-allowed pointer-events-none'
                    : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50 cursor-pointer'
                }`}
                title="Next Month"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* View: Months Picker */}
          {viewMode === 'months' && (
            <div className="p-3 grid grid-cols-3 gap-2">
              {MONTH_NAMES.map((name, idx) => {
                const isSelected = viewMonth === idx;
                const isMonthBlocked = isMonthDisabled(idx);
                return (
                  <button
                    key={name}
                    type="button"
                    disabled={isMonthBlocked}
                    onClick={() => {
                      if (isMonthBlocked) return;
                      setViewMonth(idx);
                      setViewMode('days');
                    }}
                    className={`py-2 px-1 text-xs font-semibold rounded-xl transition-all ${
                      isMonthBlocked
                        ? 'text-gray-300 opacity-25 cursor-not-allowed pointer-events-none'
                        : isSelected
                        ? 'bg-gradient-to-r from-sky-500 to-fuchsia-500 text-white shadow-xs cursor-pointer'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer'
                    }`}
                  >
                    {MONTH_SHORT[idx]}
                  </button>
                );
              })}
            </div>
          )}

          {/* View: Years Picker */}
          {viewMode === 'years' && (
            <div className="p-3 grid grid-cols-3 gap-2 max-h-52 overflow-y-auto">
              {yearPickerRange.map((yr) => {
                const isSelected = viewYear === yr;
                const isYearBlocked = isYearDisabled(yr);
                return (
                  <button
                    key={yr}
                    type="button"
                    disabled={isYearBlocked}
                    onClick={() => {
                      if (isYearBlocked) return;
                      setViewYear(yr);
                      setViewMode('days');
                    }}
                    className={`py-2 px-1 text-xs font-semibold rounded-xl transition-all ${
                      isYearBlocked
                        ? 'text-gray-300 opacity-25 cursor-not-allowed pointer-events-none'
                        : isSelected
                        ? 'bg-gradient-to-r from-sky-500 to-fuchsia-500 text-white shadow-xs cursor-pointer'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer'
                    }`}
                  >
                    {yr}
                  </button>
                );
              })}
            </div>
          )}

          {/* View: Days Calendar Grid */}
          {viewMode === 'days' && (
            <div className="p-3">
              {/* Day of Week Labels */}
              <div className="grid grid-cols-7 mb-1.5 text-center">
                {DAY_NAMES.map((day) => (
                  <div
                    key={day}
                    className="text-[10px] font-bold text-gray-400 uppercase tracking-wider py-1"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Day Cells */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((item, index) => {
                  const isSelected = value && item.dateStr === value;
                  const isToday = item.dateStr === today.str;

                  return (
                    <button
                      key={index}
                      type="button"
                      disabled={item.isDisabled}
                      onClick={(e) => {
                        if (item.isDisabled) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        handleSelectDate(item.year, item.month, item.day);
                      }}
                      className={`relative h-8 w-8 sm:h-9 sm:w-9 mx-auto rounded-xl flex items-center justify-center text-xs font-medium transition-all ${
                        item.isDisabled
                          ? 'text-gray-300 opacity-25 cursor-not-allowed bg-transparent pointer-events-none select-none'
                          : isSelected
                          ? 'text-white font-bold shadow-sm ring-2 ring-sky-400/40 cursor-pointer'
                          : item.isCurrentMonth
                          ? 'text-gray-800 hover:bg-sky-50 hover:text-sky-600 cursor-pointer'
                          : 'text-gray-300 hover:bg-gray-50 cursor-pointer'
                      } ${
                        isToday && !isSelected
                          ? 'border border-blue-400 font-bold text-blue-600'
                          : ''
                      }`}
                      style={
                        isSelected
                          ? {
                              background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
                            }
                          : undefined
                      }
                    >
                      {item.day}
                      {isToday && !isSelected && (
                        <span className="absolute bottom-0.5 w-1 h-1 bg-blue-500 rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Calendar Footer */}
          <div className="px-3.5 py-2.5 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-500 hover:text-rose-600 font-medium transition-colors cursor-pointer"
            >
              Clear
            </button>

            <button
              type="button"
              disabled={isDateDisabled(today.str)}
              onClick={handleSelectToday}
              className={`font-semibold px-2.5 py-1 rounded-lg transition-colors flex items-center gap-1 ${
                isDateDisabled(today.str)
                  ? 'text-gray-300 opacity-40 cursor-not-allowed pointer-events-none'
                  : 'text-blue-600 hover:text-blue-700 bg-blue-50/80 hover:bg-blue-100/80 cursor-pointer'
              }`}
            >
              <Sparkles size={11} className="text-blue-500" />
              <span>Today</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiumDatePicker;
